import L from 'leaflet';

// The zoomed-out listing markers in both views: a small rounded square in the home type's colour with a white
// edge (the owner, 2026-11-01: the apartment square of design 2 "Former" on the "Prickar, flikar och kartflik"
// canvas, for every type, the colour telling them apart). They're there to keep a map with thousands of homes
// quick and uncluttered, so each look is drawn once into a small bitmap (a sprite) and every marker is one
// drawImage of it: no more work per marker than the circles they replaced (measured on the dot canvas).

// Rounded like Leaflet's own canvas circles (Canvas._updateCircle), which these replace.
const roundRadius = (radius) => Math.max(Math.round(radius), 1);

// The square fits the marker's box (half-size r), as the circle did, so Leaflet's redraw bounds still cover it.
const SQUARE_HALF = 0.86;
const SQUARE_CORNER = 0.32;

const traceSquare = (ctx, x, y, r) => {
    const half = r * SQUARE_HALF;
    const corner = r * SQUARE_CORNER;
    ctx.beginPath();
    ctx.moveTo(x - half + corner, y - half);
    ctx.arcTo(x + half, y - half, x + half, y + half, corner);
    ctx.arcTo(x + half, y + half, x - half, y + half, corner);
    ctx.arcTo(x - half, y + half, x - half, y - half, corner);
    ctx.arcTo(x - half, y - half, x + half, y - half, corner);
    ctx.closePath();
};

// The same square as an SVG path, for Leaflet's SVG renderer (listvy draws its dots with SVG in dark mode).
const squarePath = (x, y, r) => {
    const h = r * SQUARE_HALF;
    const c = r * SQUARE_CORNER;
    return `M${x - h + c} ${y - h}H${x + h - c}A${c} ${c} 0 0 1 ${x + h} ${y - h + c}`
        + `V${y + h - c}A${c} ${c} 0 0 1 ${x + h - c} ${y + h}H${x - h + c}`
        + `A${c} ${c} 0 0 1 ${x - h} ${y + h - c}V${y - h + c}A${c} ${c} 0 0 1 ${x - h + c} ${y - h}Z`;
};

// One bitmap per look (colours, edge, size and the canvas's pixel ratio), drawn like Leaflet draws a path:
// filled, then the edge stroked on the outline with round joins. Its half-size is a whole number of device
// pixels, so drawn at a snapped position it maps onto the canvas pixel for pixel.
const SPRITE_CACHE_LIMIT = 400;
const spriteCache = new Map();
export const dotSprite = (fill, stroke, weight, radius, ratio) => {
    const r = roundRadius(radius);
    const edge = stroke && weight > 0 ? weight : 0;
    const key = `${fill}|${edge ? stroke : ''}|${edge}|${r}|${ratio}`;
    let sprite = spriteCache.get(key);
    if (sprite) return sprite;
    const half = Math.ceil((r + (edge / 2) + 1) * ratio) / ratio;
    const pixels = Math.round(half * 2 * ratio);
    const canvas = document.createElement('canvas');
    canvas.width = pixels;
    canvas.height = pixels;
    const ctx = canvas.getContext('2d');
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    traceSquare(ctx, half, half, r);
    ctx.fillStyle = fill;
    ctx.fill();
    if (edge) {
        ctx.lineWidth = edge;
        ctx.strokeStyle = stroke;
        ctx.lineJoin = 'round';
        ctx.stroke();
    }
    // A big screen's canvas ratio follows the window's size (Map.js's pixel budget), so looks can pile up.
    if (spriteCache.size >= SPRITE_CACHE_LIMIT) spriteCache.clear();
    sprite = { canvas, half, size: half * 2 };
    spriteCache.set(key, sprite);
    return sprite;
};

// Draws a sprite centred on (x, y), its corner snapped to the canvas's device pixels. origin: the canvas's top
// left in the same coordinates (Leaflet's canvas draws in layer points from its bounds' corner).
export const drawDotSprite = (ctx, sprite, x, y, ratio, originX = 0, originY = 0) => {
    ctx.drawImage(
        sprite.canvas,
        (Math.round((x - sprite.half - originX) * ratio) / ratio) + originX,
        (Math.round((y - sprite.half - originY) * ratio) / ratio) + originY,
        sprite.size,
        sprite.size,
    );
};

// The sprite as an image, for listvy's zoomed-out groups (DOM markers).
const urlCache = new Map();
export const dotSpriteUrl = (fill, stroke, weight, radius, ratio) => {
    const sprite = dotSprite(fill, stroke, weight, radius, ratio);
    let url = urlCache.get(sprite);
    if (!url) {
        url = sprite.canvas.toDataURL('image/png');
        if (urlCache.size >= SPRITE_CACHE_LIMIT) urlCache.clear();
        urlCache.set(sprite, url);
    }
    return { url, size: sprite.size };
};

// A circle marker drawn as the square. On a canvas renderer it's one sprite; on an SVG renderer the square's path.
export const DotSquareMarker = L.CircleMarker.extend({
    _updatePath() {
        const renderer = this._renderer;
        if (!(renderer instanceof L.Canvas)) {
            if (typeof renderer._setPath === 'function') {
                renderer._setPath(this, this._empty() ? 'M0 0' : squarePath(this._point.x, this._point.y, roundRadius(this._radius)));
            }
            return;
        }
        if (!renderer._drawing || this._empty()) return;
        const ratio = renderer._pixelRatio || (L.Browser.retina ? 2 : 1);
        // Looked up once per marker (and again only when the canvas's ratio or the look changes): every redraw
        // draws thousands of these, and building the cache key each time cost more than the drawing.
        if (!this._dotSprite || this._dotSpriteRatio !== ratio) {
            const { fillColor, color, weight, stroke } = this.options;
            this._dotSprite = dotSprite(fillColor || color, stroke ? color : null, stroke ? weight : 0, this._radius, ratio);
            this._dotSpriteRatio = ratio;
        }
        const ctx = renderer._ctx;
        // The layer drawn before may have left its own opacity on the context (a spread leg's line).
        ctx.globalAlpha = 1;
        drawDotSprite(ctx, this._dotSprite, this._point.x, this._point.y, ratio, renderer._bounds.min.x, renderer._bounds.min.y);
    },
    setStyle(style) {
        this._dotSprite = null;
        return L.CircleMarker.prototype.setStyle.call(this, style);
    },
    setRadius(radius) {
        this._dotSprite = null;
        return L.CircleMarker.prototype.setRadius.call(this, radius);
    },
});
